import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const LicenseContext = createContext();

export const useLicense = () => {
  const context = useContext(LicenseContext);
  if (!context) {
    throw new Error("useLicense must be used within LicenseProvider");
  }
  return context;
};

// الصفحات المتاحة لكل نسخة
export const LICENSE_FEATURES = {
  regular: [
    "/dashboard",
    "/pos",
    "/inventory",
    "/customers",
    "/suppliers",
    "/expenses",
    "/sales",
    "/purchases",
    "/customer-debts",
    "/supplier-debts",
    "/damages",
    "/settings",
    "/security",
  ],
  professional: [
    // كل صفحات النسخة العادية
    "/dashboard",
    "/pos",
    "/inventory",
    "/customers",
    "/suppliers",
    "/expenses",
    "/sales",
    "/purchases",
    "/customer-debts",
    "/supplier-debts",
    "/damages",
    "/settings",
    "/security",
    // الصفحات الإضافية
    "/reports",
    "/live",
    "/cash-register",
    "/installments",
    "/employees",
    "/returns",
    "/contracts",
    "/expiry-alerts",
    "/backup",
    "/roles",
    "/labels-settings",
    "/theme",
  ],
};

// الصفحات الاحترافية فقط (للقفل)
export const PROFESSIONAL_ONLY_PAGES = [
  "/reports",
  "/live",
  "/cash-register",
  "/installments",
  "/employees",
  "/returns",
  "/contracts",
  "/expiry-alerts",
  "/backup",
  "/roles",
  "/labels-settings",
  "/theme",
];

// أسعار الباقات
export const PRICING = {
  regular: {
    name: "العادية",
    nameEn: "Regular",
    cash: 500,
    installment: {
      perPayment: 200,
      payments: 3,
      total: 600,
    },
  },
  professional: {
    name: "الاحترافية",
    nameEn: "Professional",
    cash: 1000,
    installment: {
      perPayment: 300,
      payments: 4,
      total: 1200,
    },
  },
};

// قائمة المالكين - يتم التحقق منهم عبر role super_admin في الباك إند
const OWNER_EMAILS = [
  "aomerali42@gmail.com",
];

export const LicenseProvider = ({ children }) => {
  const [license, setLicense] = useState({
    type: "regular", // regular | professional
    status: "trial", // trial | active | expired
    paymentType: null, // cash | installment
    paidAmount: 0,
    totalAmount: 0,
    installmentsPaid: 0,
    totalInstallments: 0,
    expiresAt: null,
    activatedAt: null,
  });
  const [loading, setLoading] = useState(true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeTarget, setUpgradeTarget] = useState(null); // "professional" or feature name
  const [isOwner, setIsOwner] = useState(false);

  // الاستماع لتغيرات localStorage (عند تسجيل الدخول/الخروج)
  useEffect(() => {
    fetchLicense();
    
    // الاستماع لتغيرات storage
    const handleStorageChange = (e) => {
      if (e.key === "user" || e.key === "token") {
        fetchLicense();
      }
    };
    
    window.addEventListener("storage", handleStorageChange);
    
    // فحص دوري كل ثانيتين للتغيرات
    const interval = setInterval(() => {
      const token = localStorage.getItem("token");
      if (token && !isOwner) {
        fetchLicense();
      }
    }, 2000);
    
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(interval);
    };
  }, [isOwner]);

  const fetchLicense = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setLoading(false);
        return;
      }

      // جلب بيانات المستخدم مباشرة من السيرفر
      try {
        const userResponse = await axios.get(
          `${window.location.origin}/api/auth/me`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        const user = userResponse.data;
        const userEmail = (user?.email || "").toLowerCase().trim();
        const userRole = (user?.role || "").toLowerCase().trim();
        const licenseType = (user?.license_type || "").toLowerCase().trim();
        
        // قائمة الإيميلات المالكة (بحروف صغيرة للمقارنة)
        const ownerEmailsLower = OWNER_EMAILS.map(e => e.toLowerCase().trim());
        
        // إذا كان المستخدم مالك أو super_admin أو لديه ترخيص lifetime/full، يحصل على كل الصلاحيات
        if (ownerEmailsLower.includes(userEmail) || 
            userRole === "super_admin" || 
            userRole === "admin" ||
            licenseType === "lifetime" ||
            licenseType === "full") {
          setIsOwner(true);
          setLicense({
            type: "full",
            status: "active",
            paymentType: "cash",
            paidAmount: 690000,
            totalAmount: 690000,
            installmentsPaid: 0,
            totalInstallments: 0,
            expiresAt: null,
            activatedAt: new Date().toISOString(),
          });
          setLoading(false);
          console.log("✅ Owner/Admin detected:", userEmail, "Role:", userRole, "License:", licenseType);
          return;
        }
      } catch (err) {
        console.error("Error fetching user for license:", err);
      }

      // إذا لم يكن مالك، جلب حالة الترخيص العادية
      const response = await axios.get(
        `${window.location.origin}/api/license/status`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLicense(response.data);
    } catch (error) {
      console.error("Error fetching license:", error);
    } finally {
      setLoading(false);
    }
  };

  // التحقق من إمكانية الوصول لصفحة
  const canAccessPage = (path) => {
    if (isOwner) return true; // المالك يدخل كل شيء
    // 🔓 النسخة التجريبية تحصل على كل المزايا (5 أيام full access)
    if (license.status === "trial") return true;
    if (license.type === "professional" || license.type === "full" || license.type === "lifetime") return true;
    return LICENSE_FEATURES.regular.includes(path);
  };

  // التحقق إذا الصفحة احترافية فقط
  const isProfessionalOnly = (path) => {
    return PROFESSIONAL_ONLY_PAGES.includes(path);
  };

  // فتح نافذة الترقية
  const promptUpgrade = (target = "professional") => {
    setUpgradeTarget(target);
    setShowUpgradeModal(true);
  };

  // إغلاق نافذة الترقية
  const closeUpgradeModal = () => {
    setShowUpgradeModal(false);
    setUpgradeTarget(null);
  };

  // 🔓 التجريبي = صلاحيات احترافية كاملة طيلة فترة التجربة
  const hasFullAccess =
    isOwner ||
    license.status === "trial" ||
    license.type === "professional" ||
    license.type === "full" ||
    license.type === "lifetime";

  const value = {
    license,
    loading,
    canAccessPage,
    isProfessionalOnly,
    promptUpgrade,
    closeUpgradeModal,
    showUpgradeModal,
    upgradeTarget,
    refreshLicense: fetchLicense,
    isRegular: license.type === "regular" && !isOwner && license.status !== "trial",
    isProfessional: hasFullAccess,
    isOwner,
    PRICING,
  };

  return (
    <LicenseContext.Provider value={value}>
      {children}
    </LicenseContext.Provider>
  );
};

export default LicenseContext;
